import { findPrereqProblems } from './graph'
import { compareQuestIds } from './sort'
import type { CatalogIndex } from '../catalog/types'
import type { Prereq, Quest } from './types'

export type NodeKind = 'quest' | 'gate'

export interface GraphNode {
  id: string
  label: string
  tag: string
  gatingLevel: number
  disabled: boolean
  /** Layer index — longest prereq chain leading here. */
  depth: number
  x: number
  y: number
  kind: NodeKind
}

export interface GraphEdge {
  from: string
  to: string
  /** Part of a prereq cycle: these quests can never appear in game. */
  broken: boolean
  /** Drawn on the path — the visit count for a location gate. */
  label?: string
}

export interface GraphLayout {
  nodes: GraphNode[]
  edges: GraphEdge[]
  /** Work-in-progress quests, parked below rather than drawn in the graph. */
  bank: GraphNode[]
  /** No prereqs of any kind, and nothing depends on them. */
  orphaned: GraphNode[]
  /** Quests caught in a prereq cycle: they can never appear in game. */
  cyclicIds: string[]
  width: number
  height: number
}

export const NODE_WIDTH = 176
export const NODE_HEIGHT = 46
/** Gates are drawn smaller inside the same grid cell, so layout math stays uniform. */
export const GATE_WIDTH = 150
export const GATE_HEIGHT = 30
const COLUMN_GAP = 84
const ROW_GAP = 14
/** Blank rows between bands, so separate chains read as separate. */
const BAND_GAP = 1

/** How much a chain belongs at the top. Lower sorts higher. */
const TAG_WEIGHT: Record<string, number> = {
  TUTORIAL: 0,
  MAIN: 0,
  LOCATION: 1,
  TOOL: 1,
  SIDE: 1,
}

/** Stable key so every quest behind the same condition shares one gate node. */
function gateKey(prereq: Prereq): string {
  if (prereq.type === 'QuestCompleted') return `gate:QuestCompleted:${prereq.quest}`
  // One gate per location: the visit count rides on the edge instead, so two
  // requirements on the same place share a node rather than duplicating it.
  if (prereq.type === 'LocationVisit') return `gate:LocationVisit:${prereq.location}`
  return `gate:${prereq.type}:${prereq.value}`
}

/** What the gate node says. Falls back to the raw id when the catalog lacks it. */
export function describePrereq(prereq: Prereq, catalog?: CatalogIndex): string {
  switch (prereq.type) {
    case 'QuestCompleted':
      return prereq.quest
    case 'LocationVisit':
      // The count lives on the edge, so the node names the place alone.
      return catalog?.locations.get(prereq.location)?.title ?? prereq.location
    case 'RenownLevel':
      return `Renown ${prereq.value}`
    case 'TotalLevel':
      return `Total level ${prereq.value}`
    default:
      return `${prereq.type} ${prereq.value}`
  }
}

/**
 * Layered left-to-right layout. Prereqs form a DAG, so placing each quest one
 * column right of its deepest prerequisite makes every chain read as a left-to-right
 * line, which a force-directed layout would turn into a hairball.
 *
 * A quest opened by a condition rather than another quest hangs off a gate node
 * naming that condition, so it sits in the graph with its reason visible instead of
 * being exiled to a list. Quests behind the same condition share one gate.
 */
export function layoutQuestGraph(quests: Quest[], catalog?: CatalogIndex): GraphLayout {
  const byId = new Map(quests.map((quest) => [quest.id, quest]))

  const parents = new Map<string, string[]>()
  const children = new Map<string, string[]>()
  for (const quest of quests) {
    parents.set(quest.id, [])
    children.set(quest.id, [])
  }
  const edges: GraphEdge[] = []
  for (const quest of quests) {
    for (const prereq of quest.prereqs) {
      if (prereq.type !== 'QuestCompleted') continue
      if (!byId.has(prereq.quest)) continue
      parents.get(quest.id)!.push(prereq.quest)
      children.get(prereq.quest)!.push(quest.id)
      edges.push({ from: prereq.quest, to: quest.id, broken: false })
    }
  }

  // Cycle membership comes from the same checker the editor warns with, so the graph
  // and the banner can never disagree about which quests are unreachable.
  const cyclic = new Set(
    findPrereqProblems(quests)
      .filter((problem) => problem.kind === 'cycle')
      .flatMap((problem) => problem.questIds),
  )
  for (const edge of edges) {
    if (edge.from === edge.to || (cyclic.has(edge.from) && cyclic.has(edge.to))) {
      edge.broken = true
    }
  }

  // A work-in-progress quest is parked below — but only when nothing hangs off it,
  // since pulling a quest out from the middle of a chain would break the picture.
  const banked = quests.filter(
    (quest) =>
      quest.disabled &&
      parents.get(quest.id)!.length === 0 &&
      children.get(quest.id)!.length === 0,
  )
  const bankedIds = new Set(banked.map((quest) => quest.id))
  const drawn = quests.filter((quest) => !bankedIds.has(quest.id))

  // Gates: only for quests nothing else leads to, so a mid-chain quest that also
  // carries a level requirement doesn't sprout a redundant node.
  const gateNodes = new Map<string, GraphNode>()
  for (const quest of drawn) {
    if (parents.get(quest.id)!.length > 0) continue
    for (const prereq of quest.prereqs) {
      if (prereq.type === 'QuestCompleted') continue
      const key = gateKey(prereq)
      if (!gateNodes.has(key)) {
        gateNodes.set(key, {
          id: key,
          label: describePrereq(prereq, catalog),
          tag: 'GATE',
          gatingLevel: 0,
          disabled: false,
          depth: 0,
          x: 0,
          y: 0,
          kind: 'gate',
        })
      }
      edges.push({
        from: key,
        to: quest.id,
        broken: false,
        ...(prereq.type === 'LocationVisit' ? { label: `×${prereq.value}` } : {}),
      })
      parents.get(quest.id)!.push(key)
    }
  }

  // A location is itself opened by a quest, so a location gate hangs off that quest
  // rather than floating at the left edge. That keeps one continuous tree:
  // quest -> location -> quest -> location.
  for (const [key, gate] of gateNodes) {
    parents.set(key, [])
    const locationId = key.startsWith('gate:LocationVisit:')
      ? key.slice('gate:LocationVisit:'.length)
      : null
    if (!locationId) continue
    for (const questId of catalog?.locations.get(locationId)?.prereqs ?? []) {
      if (!byId.has(questId) || bankedIds.has(questId)) continue
      parents.get(key)!.push(questId)
      edges.push({ from: questId, to: key, broken: false })
      gate.tag = 'GATE'
    }
  }

  // Longest-path depth. A quest inside a cycle has no well-defined depth, so the
  // walk guards against re-entry rather than recursing forever.
  const depths = new Map<string, number>()
  const inProgress = new Set<string>()
  function depthOf(id: string): number {
    const cached = depths.get(id)
    if (cached !== undefined) return cached
    if (inProgress.has(id)) return 0
    inProgress.add(id)
    let depth = 0
    for (const parent of parents.get(id) ?? []) {
      if (parent === id) continue
      depth = Math.max(depth, depthOf(parent) + 1)
    }
    inProgress.delete(id)
    depths.set(id, depth)
    return depth
  }
  for (const quest of drawn) depthOf(quest.id)
  for (const key of gateNodes.keys()) depthOf(key)

  const linked = drawn.filter(
    (quest) => parents.get(quest.id)!.length > 0 || children.get(quest.id)!.length > 0,
  )
  const orphans = drawn.filter(
    (quest) => parents.get(quest.id)!.length === 0 && children.get(quest.id)!.length === 0,
  )

  const toNode = (quest: Quest): GraphNode => ({
    id: quest.id,
    label: quest.label || quest.id,
    tag: quest.tag,
    gatingLevel: quest.computed.gatingLevel,
    disabled: quest.disabled ?? false,
    depth: depths.get(quest.id) ?? 0,
    x: 0,
    y: 0,
    kind: 'quest',
  })

  // Group into columns, seeded in id order so the result is deterministic.
  const columns: GraphNode[][] = []
  for (const gate of gateNodes.values()) {
    gate.depth = depths.get(gate.id) ?? 0
    ;(columns[gate.depth] ??= []).push(gate)
  }
  for (const quest of [...linked].sort((a, b) => compareQuestIds(a.id, b.id))) {
    const node = toNode(quest)
    ;(columns[node.depth] ??= []).push(node)
  }

  // Barycentre sweeps: pull each node toward the average row of its neighbours so
  // edges run roughly straight instead of criss-crossing.
  const rowOf = new Map<string, number>()
  columns.forEach((column) => column.forEach((node, index) => rowOf.set(node.id, index)))

  // Edges now run quest->quest, quest->gate and gate->quest, so read children off
  // the edge list rather than the quest-only map.
  const childIndex = new Map<string, string[]>()
  for (const edge of edges) {
    const list = childIndex.get(edge.from)
    if (list) list.push(edge.to)
    else childIndex.set(edge.from, [edge.to])
  }
  const childrenOf = (id: string): string[] => childIndex.get(id) ?? []

  for (let pass = 0; pass < 4; pass += 1) {
    const forward = pass % 2 === 0
    const order = forward
      ? columns.map((_, index) => index)
      : columns.map((_, index) => columns.length - 1 - index)
    for (const columnIndex of order) {
      const column = columns[columnIndex]
      if (!column || column.length < 2) continue
      const scored = column.map((node) => {
        const neighbours = forward ? (parents.get(node.id) ?? []) : childrenOf(node.id)
        const rows = neighbours
          .map((other) => rowOf.get(other))
          .filter((row): row is number => row !== undefined)
        const barycentre =
          rows.length > 0 ? rows.reduce((sum, row) => sum + row, 0) / rows.length : rowOf.get(node.id)!
        return { node, barycentre }
      })
      scored.sort((a, b) => a.barycentre - b.barycentre || compareQuestIds(a.node.id, b.node.id))
      columns[columnIndex] = scored.map((entry) => entry.node)
      columns[columnIndex]!.forEach((node, index) => rowOf.set(node.id, index))
    }
  }

  // Separate chains are stacked as bands rather than interleaved, so the tutorial
  // and main spine reads across the top and the location chains sit beneath it.
  const band = assignBands(columns.flat(), edges)
  for (const column of columns) {
    if (!column) continue
    column.sort(
      (a, b) =>
        (band.get(a.id) ?? 0) - (band.get(b.id) ?? 0) ||
        (rowOf.get(a.id) ?? 0) - (rowOf.get(b.id) ?? 0),
    )
  }

  // Each band gets as many rows as its widest column needs, so bands never overlap.
  const bandCount = Math.max(0, ...[...band.values()]) + 1
  const bandRows: number[] = Array.from({ length: bandCount }, (_, b) =>
    Math.max(0, ...columns.map((column) => (column ?? []).filter((n) => band.get(n.id) === b).length)),
  )
  const bandStart: number[] = []
  let cursor = 0
  for (let b = 0; b < bandCount; b += 1) {
    bandStart[b] = cursor
    cursor += bandRows[b]! + (bandRows[b]! > 0 ? BAND_GAP : 0)
  }

  columns.forEach((column, columnIndex) => {
    if (!column) return
    const usedInBand = new Map<number, number>()
    for (const node of column) {
      const b = band.get(node.id) ?? 0
      const offset = usedInBand.get(b) ?? 0
      usedInBand.set(b, offset + 1)
      node.x = columnIndex * (NODE_WIDTH + COLUMN_GAP)
      node.y = (bandStart[b]! + offset) * (NODE_HEIGHT + ROW_GAP)
    }
  })

  const nodes = columns.flat()
  const byIdOrder = (a: Quest, b: Quest) => compareQuestIds(a.id, b.id)
  const bank = [...banked].sort(byIdOrder).map(toNode)
  const orphaned = [...orphans].sort(byIdOrder).map(toNode)

  const width = columns.length * (NODE_WIDTH + COLUMN_GAP)
  const height = Math.max(1, cursor) * (NODE_HEIGHT + ROW_GAP)

  return { nodes, edges, bank, orphaned, cyclicIds: [...cyclic], width, height }
}

/**
 * Split the graph into its separate chains and order them top to bottom, most
 * tutorial/main first. Ordering is derived, not stored, so adding a quest re-bands
 * the map on its own rather than leaving hand-placed coordinates to go stale.
 */
function assignBands(nodes: GraphNode[], edges: GraphEdge[]): Map<string, number> {
  const neighbours = new Map<string, string[]>()
  const link = (a: string, b: string) => {
    const list = neighbours.get(a)
    if (list) list.push(b)
    else neighbours.set(a, [b])
  }
  for (const edge of edges) {
    link(edge.from, edge.to)
    link(edge.to, edge.from)
  }

  const byId = new Map(nodes.map((node) => [node.id, node]))
  const seen = new Set<string>()
  const components: GraphNode[][] = []
  for (const node of nodes) {
    if (seen.has(node.id)) continue
    seen.add(node.id)
    const stack = [node.id]
    const group: GraphNode[] = []
    while (stack.length > 0) {
      const id = stack.pop()!
      const member = byId.get(id)
      if (member) group.push(member)
      for (const next of neighbours.get(id) ?? []) {
        if (seen.has(next)) continue
        seen.add(next)
        stack.push(next)
      }
    }
    components.push(group)
  }

  const scored = components.map((group) => {
    const quests = group.filter((node) => node.kind === 'quest')
    const weight = quests.length
      ? quests.reduce((sum, node) => sum + (TAG_WEIGHT[node.tag] ?? 1), 0) / quests.length
      : 1
    return { group, weight, size: quests.length, first: [...group].sort((a, b) => compareQuestIds(a.id, b.id))[0]!.id }
  })
  // Most tutorial/main first; bigger chains before smaller at the same weight.
  scored.sort(
    (a, b) => a.weight - b.weight || b.size - a.size || compareQuestIds(a.first, b.first),
  )

  const band = new Map<string, number>()
  scored.forEach((entry, index) => {
    for (const node of entry.group) band.set(node.id, index)
  })
  return band
}

/** Everything upstream and downstream of one quest — its whole chain. */
export function chainOf(
  id: string,
  edges: GraphEdge[],
): { ancestors: Set<string>; descendants: Set<string> } {
  const up = new Map<string, string[]>()
  const down = new Map<string, string[]>()
  for (const edge of edges) {
    ;(up.get(edge.to) ?? up.set(edge.to, []).get(edge.to)!).push(edge.from)
    ;(down.get(edge.from) ?? down.set(edge.from, []).get(edge.from)!).push(edge.to)
  }
  const walk = (start: string, map: Map<string, string[]>): Set<string> => {
    const seen = new Set<string>()
    const queue = [...(map.get(start) ?? [])]
    while (queue.length > 0) {
      const next = queue.pop()!
      if (seen.has(next)) continue
      seen.add(next)
      queue.push(...(map.get(next) ?? []))
    }
    return seen
  }
  return { ancestors: walk(id, up), descendants: walk(id, down) }
}
